import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { ConnectionSettings, CopilotStudioClient, CopilotStudioWebChat } from "@microsoft/agents-copilotstudio-client";

import { config } from "@/config";

import type { CopilotStudioWebChatConnection } from "@microsoft/agents-copilotstudio-client";

let msalInstance: PublicClientApplication | undefined;
// Single-flights concurrent acquireToken calls (e.g. the panel mounting twice) onto one
// loginPopup, since MSAL itself throws interaction_in_progress rather than de-duping.
let acquireTokenPromise: Promise<string> | undefined;

// Ported from Spike/CopilotStudioSpike/acquireToken.js — this environment's Copilot Studio
// agent only exposes the Dataverse-backed "Direct to Engine" endpoint (Entra ID auth), not a
// Direct Line channel, so MSAL interactive sign-in is required rather than a Direct Line secret.
async function acquireToken(agentConfig: NonNullable<typeof config.agent>): Promise<string> {
  acquireTokenPromise ??= acquireTokenNow(agentConfig).finally(() => {
    acquireTokenPromise = undefined;
  });
  return acquireTokenPromise;
}

async function acquireTokenNow(agentConfig: NonNullable<typeof config.agent>): Promise<string> {
  msalInstance ??= new PublicClientApplication({
    auth: {
      clientId: agentConfig.appClientId,
      authority: `${agentConfig.authority ?? "https://login.microsoftonline.com"}/${agentConfig.tenantId}`,
    },
  });
  await msalInstance.initialize();

  const loginRequest = {
    scopes: [CopilotStudioClient.scopeFromSettings(new ConnectionSettings(agentConfig))],
    // A dedicated static page rather than the SPA root — the popup would otherwise load the full
    // app under the site's COOP/COEP headers, which severs the opener's handle to the popup
    // before MSAL can detect the redirect and close it.
    redirectUri: `${window.location.origin}/auth-popup.html`,
  };

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    try {
      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      return response.accessToken;
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) throw error;
    }
  }

  try {
    const response = await msalInstance.loginPopup({ ...loginRequest, prompt: "select_account" });
    return response.accessToken;
  } catch (error) {
    // MSAL leaves a stale "interaction in progress" flag in sessionStorage if a previous popup
    // was interrupted (closed without completing, or a dev-server hot reload tore down the page
    // mid-flow) instead of running to completion — it never clears the flag itself, so every
    // later loginPopup call fails immediately with this error even though nothing is actually
    // in flight. There's no public MSAL API to clear it — removing the sessionStorage key
    // directly and retrying once is the documented community workaround for this MSAL bug
    // (AzureAD/microsoft-authentication-library-for-js#3005).
    if (error instanceof BrowserAuthError && error.errorCode === "interaction_in_progress") {
      Object.keys(sessionStorage)
        .filter((key) => key.endsWith(".interaction.status"))
        .forEach((key) => sessionStorage.removeItem(key));

      const response = await msalInstance.loginPopup({ ...loginRequest, prompt: "select_account" });
      return response.accessToken;
    }
    throw error;
  }
}

/**
 * Opens a Copilot Studio "Direct to Engine" connection, WebChat-compatible via
 * CopilotStudioWebChat.createConnection (proven in Spike/CopilotStudioSpike).
 *
 * CopilotStudioWebChat.createConnection only starts the underlying conversation lazily,
 * the first time WebChat itself subscribes to connection.activity$ — it calls
 * client.startConversationAsync() internally. There is no way to start the conversation
 * ourselves ahead of that to learn the conversation id early without creating a second,
 * unused conversation, and subscribing to connection.activity$ independently here would race
 * WebChat's own internal prime/subscribe handshake. So context seeding (see AgentAssistant.vue)
 * instead reads the conversation id off the DIRECT_LINE/INCOMING_ACTIVITY action WebChat's own
 * store dispatches, via a store middleware — observing WebChat's single canonical subscription
 * rather than adding a competing one.
 */
export async function createAgentAssistantConnection(): Promise<CopilotStudioWebChatConnection> {
  const agentConfig = config.agent;

  if (agentConfig == null) {
    throw new Error("Agent Assistant is not configured for this environment (missing config.json 'agent' block).");
  }

  const settings = new ConnectionSettings(agentConfig);
  const token = await acquireToken(agentConfig);
  const client = new CopilotStudioClient(settings, token);

  return CopilotStudioWebChat.createConnection(client, { showTyping: true });
}
